/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { GuidedExpressionEditorUtils } from "abreact/expression/ui/editor/impl/utils/GuidedExpressionEditorUtils";
import { AbImmutableRecord } from "abreact/utils/AbImmutableRecord";
import { ExpExpressionNodeNodeTypeEnum } from "abweb/generated/abwebinternal/api/dto/ExpExpressionNode";
import { Assert } from "abweb/utils/Assert";
import Immutable from "immutable";
// import AbstractOperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.AbstractOperandValue;
// import OperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperandValue;
// import OperatorOperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue;
// import AbstractRowBasedModel = GuidedExpressionEditor.AbstractRowBasedModel;

const defaultState = {
  id: undefined,
  sourceOperandValue: undefined,
  operatorOperandValue: undefined,
  argumentOperandValuesByOperandName: Immutable.Map(),
  isDisabled: false
};

/**
 * @author fgracely
 */
export class RowBasedRowModelImpl extends AbImmutableRecord.Class(defaultState) {
  static create(id) {
    return new RowBasedRowModelImpl(id);
  }
  static fromOperatorOperandValue(operatorOperandValue) {
    return new RowBasedRowModelImpl(operatorOperandValue.id, operatorOperandValue);
  }

  /**
   * If {@link operatorOperandValue#expOperator} has role ABSENT, we know that no real {@link GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue}
   * exists, and that instead only a source {@link GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperandValue} has been selected for this row. Please see the
   * associated comment in
   * {@link OperatorOperandExpressionNodeConverter#resolveExpOperator} and {@link RowBasedRowModelImpl#export}.
   */
  constructor(id, operatorOperandValue) {
    let sourceOperandValue;
    let argumentOperandValuesByOperandName;
    let isDisabled;
    if (operatorOperandValue != null) {
      sourceOperandValue = operatorOperandValue.sourceOperandValue;
      argumentOperandValuesByOperandName = operatorOperandValue.argumentOperandValuesByOperandName;
      isDisabled = operatorOperandValue.isDisabled;
      if (Expression.Domain.OperatorRole.isAbsent(operatorOperandValue.expOperator)) {
        operatorOperandValue = undefined;
      } else {
        operatorOperandValue = operatorOperandValue.withoutSourceOperandValue().withoutArgumentOperandValues();
      }
    }
    super({
      id,
      sourceOperandValue,
      operatorOperandValue,
      argumentOperandValuesByOperandName,
      isDisabled
    });
  }
  isEmpty() {
    return this.sourceOperandValue == null && this.operatorOperandValue == null && this.argumentOperandValuesByOperandName.isEmpty();
  }
  isComplete() {
    if (this.sourceOperandValue == null || this.operatorOperandValue == null) {
      return false;
    }
    const {
      isPlaceholder,
      expOperator
    } = this.operatorOperandValue;
    return !isPlaceholder && GuidedExpressionEditorUtils.isExpOperatorComplete(expOperator, this.sourceOperandValue, this.argumentOperandValuesByOperandName);
  }
  withId(id) {
    return this.with({
      id,
      operatorOperandValue: this.operatorOperandValue?.withId(id)
    });
  }
  withSourceOperandValue(sourceOperandValue) {
    return this.with({
      sourceOperandValue
    });
  }
  withOperatorOperandValue(operatorOperandValue) {
    let sourceOperandValue = this.sourceOperandValue;
    if (operatorOperandValue != null) {
      Assert.assert(operatorOperandValue.id === this.id);
      sourceOperandValue = sourceOperandValue?.withOperandName(operatorOperandValue.expOperator.sourceOperandName);
    }
    return this.with({
      sourceOperandValue,
      operatorOperandValue
    });
  }
  withArgumentOperandValue(operandName, operandValue) {
    let argumentOperandValuesByOperandName = this.argumentOperandValuesByOperandName;
    if (operandValue != null) {
      argumentOperandValuesByOperandName = argumentOperandValuesByOperandName.set(operandName, operandValue);
    } else {
      argumentOperandValuesByOperandName = argumentOperandValuesByOperandName.delete(operandName);
    }
    return this.withArgumentOperandValuesByOperandName(argumentOperandValuesByOperandName);
  }
  withArgumentOperandValuesByOperandName(argumentOperandValuesByOperandName) {
    return this.with({
      argumentOperandValuesByOperandName
    });
  }
  transform(transformer) {
    let {
      sourceOperandValue,
      operatorOperandValue,
      argumentOperandValuesByOperandName
    } = this;
    if (sourceOperandValue != null) {
      sourceOperandValue = transformer(sourceOperandValue);
    }
    if (operatorOperandValue != null) {
      operatorOperandValue = transformer(operatorOperandValue);
    }
    for (const operandName of argumentOperandValuesByOperandName.keySeq().toArray()) {
      argumentOperandValuesByOperandName = argumentOperandValuesByOperandName.set(operandName, transformer(argumentOperandValuesByOperandName.get(operandName)));
    }
    return this.with({
      sourceOperandValue: sourceOperandValue,
      operatorOperandValue: operatorOperandValue,
      argumentOperandValuesByOperandName: argumentOperandValuesByOperandName
    });
  }

  /**
   * {@link GuidedExpressionEditor.AbstractRowBasedModel.RowModel} is a flattened representation of a {@link ROLE_CONDITION} or
   * {@link ROLE_ACTION} operator. The result of {@link GuidedExpressionEditor.AbstractRowBasedModel.RowModel#export} must be an operator
   * {@link ExpExpressionNode} representing the all selected {@link GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperandValue}s in the row thus far. This arrangement
   * usually works naturally, but we arrive at a problem if only a source {@link GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperandValue} has been chosen. Implicit
   * in this is that no {@link GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue} has been chosen, meaning that there is no real operator
   * {@link ExpExpressionNode} around which the export's "un-flattening" can take place. It doesn't exist yet. So, to
   * defeat this, we create a sentinel operator {@link ExpExpressionNode} to which we attach the selected source operand
   * {@link ExpExpressionNode}. For more information about the implications of this when importing such an
   * {@link ExpExpressionNode}, please see the associated comments in
   * {@link OperatorOperandExpressionNodeConverter#resolveExpOperator} and {@link RowBasedRowModelImpl#constructor}.
   */
  export() {
    let expExpressionNode;
    if (this.operatorOperandValue == null) {
      expExpressionNode = {
        id: this.id,
        nodeType: ExpExpressionNodeNodeTypeEnum.OPERATOR
      };
    } else {
      expExpressionNode = this.operatorOperandValue.toExpExpressionNode();
      Assert.assert(expExpressionNode.operandValues?.length === 0);
    }
    const argumentOperandValues = this.argumentOperandValuesByOperandName.valueSeq().toArray();
    expExpressionNode.operandValues = Expression.Node.Producer.mapNullable(this.sourceOperandValue, ...argumentOperandValues);
    return expExpressionNode;
  }
}
//# sourceMappingURL=RowBasedRowModelImpl.js.map
